import { useCallback } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_PROJECT_VIEW_HEADER_LIFECYCLE_SELECT } from '@shared/constants';
import { createLocalStorageStore } from '@client/helpers/local-storage.helpers';
import {
  getSelectedLifecycles,
  setLifecycles,
  useSelectedLifecycles,
} from '@features/scope/scope.store';
import { Lifecycle, lifecycles } from '@shared/models/lifecycles.model';
import type { LifecycleGroupId } from './lifecycle-groups.data';

/** Sentinel selection toggling every lifecycle phase at once. */
export const ALL_LIFECYCLES = 'All';
export type LifecycleSelection = typeof ALL_LIFECYCLES | Lifecycle;

const LIFECYCLE_ITEMS: Lifecycle[] = [...lifecycles];

/** The lifecycle slice of the scope. Defined in {@link useScope}'s store. */
export { getSelectedLifecycles, setLifecycles, useSelectedLifecycles };

type SavedGroupStates = Partial<Record<LifecycleGroupId, Lifecycle[]>>;

const { useStore: useSavedGroupStates, set: setSavedGroupStates } =
  createLocalStorageStore<SavedGroupStates>('lifecycle_group_saved_states', {});

export { setSavedGroupStates };

/** Enable every available phase in a group without changing other groups. */
export const useEnableLifecycleGroup = () =>
  useCallback((groupLifecycles: Lifecycle[]) => {
    const selected = getSelectedLifecycles();
    const result = [...new Set([...selected, ...groupLifecycles])];
    setLifecycles(result);
    amplitudeLog(AMP_PROJECT_VIEW_HEADER_LIFECYCLE_SELECT, {
      lifecycles: result,
    });
  }, []);

/**
 * Toggle all toggleable phases in a group at once, preserving per-phase state.
 *
 * - **Turning off**: saves which phases were active in the group, then deselects them all.
 * - **Turning on**: restores the saved snapshot (so phases that were already off before the
 *   group was toggled off stay off), or selects all phases if no snapshot exists.
 *
 * Empty selections fall back to `[A1-A3]`.
 */
export const useToggleLifecycleGroup = (): ((
  groupId: LifecycleGroupId,
  groupLifecycles: Lifecycle[],
) => void) => {
  const selectedLifecycles = useSelectedLifecycles();
  const savedGroupStates = useSavedGroupStates();
  return useCallback(
    (groupId: LifecycleGroupId, groupLifecycles: Lifecycle[]) => {
      const anySelected = groupLifecycles.some((lc) =>
        selectedLifecycles.includes(lc),
      );
      let newValues: Lifecycle[];
      if (anySelected) {
        const activeInGroup = groupLifecycles.filter((lc) =>
          selectedLifecycles.includes(lc),
        );
        setSavedGroupStates({ ...savedGroupStates, [groupId]: activeInGroup });
        // No fallback here — the user explicitly toggled the whole group off.
        newValues = selectedLifecycles.filter(
          (lc) => !groupLifecycles.includes(lc),
        );
      } else {
        const saved = savedGroupStates[groupId];
        const toAdd = saved && saved.length > 0 ? saved : groupLifecycles;
        newValues = [
          ...selectedLifecycles,
          ...toAdd.filter((lc) => !selectedLifecycles.includes(lc)),
        ];
      }
      // Fallback only when turning a group ON results in empty (shouldn't happen,
      // but guards against an empty saved state restoring nothing).
      const result =
        !anySelected && newValues.length === 0 ? [Lifecycle.A1A3] : newValues;
      setLifecycles(result);
      amplitudeLog(AMP_PROJECT_VIEW_HEADER_LIFECYCLE_SELECT, {
        lifecycles: result,
      });
    },
    [selectedLifecycles, savedGroupStates],
  );
};

/**
 * Toggle a single lifecycle phase (or all phases via {@link ALL_LIFECYCLES}) in the
 * persisted scope. Empty selections fall back to `[A1-A3]`; selecting nothing is meaningless.
 */
export const useSelectLifecycle = (): ((value: LifecycleSelection) => void) => {
  const selectedLifecycles = useSelectedLifecycles();
  return useCallback(
    (value: LifecycleSelection) => {
      if (value !== ALL_LIFECYCLES && !LIFECYCLE_ITEMS.includes(value)) {
        throw new Error(`Invalid lifecycle selection: ${value}`);
      }
      let newValues: Lifecycle[] = [];
      if (value === ALL_LIFECYCLES) {
        newValues =
          lifecycles.length === selectedLifecycles.length ? [] : lifecycles;
      } else if (selectedLifecycles.includes(value)) {
        newValues = selectedLifecycles.filter((item) => item !== value);
      } else {
        newValues = [...selectedLifecycles, value];
      }

      // If new values are empty, default to A1-A3, it makes no sense to show none.
      const result = newValues.length === 0 ? [Lifecycle.A1A3] : newValues;
      setLifecycles(result);
      amplitudeLog(AMP_PROJECT_VIEW_HEADER_LIFECYCLE_SELECT, {
        lifecycles: result,
      });
    },
    [selectedLifecycles],
  );
};
